import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// Define CourseData locally since the feature doesn't exist yet
export interface CourseData {
	id: string;
	name: string;
	description?: string;
	code: string;
	duration: number;
	credits: number;
}

export interface CourseDetailsFormData {
     selectedCourse: string;
     morningSections: number;
     eveningSections: number;
     morningStudents: number;
     eveningStudents: number;
     totalStudents: number;
     attachment: string;
}

export interface LabEquipment {
     labId: number;
     equipments: Array<{
          equipmentId: number;
          availableCount: number;
     }>;
}

export interface FacultyMember {
     id?: string;
     name: string;
     qualification: number;
     designation: string;
     courses: string[];
     joiningDate: string;
     cnic: string;
     phoneNumber: string;
     salary: number;
     paymentMode: string;
     employmentType: string;
     attachment: string;
     createdAt?: string;
     updatedAt?: string;
}

export interface EquipmentFormData {
     [labId: string]: {
          [equipmentId: string]: number;
     };
}

export interface AttachmentFile {
     name: string;
     size: number;
     type: string;
     lastModified: number;
     id: string;
     objectKey?: string; // For storing S3 object key
     fileUrl?: string; // For storing file URL
}

export interface AttachmentSection {
     courseTimetable: AttachmentFile[];
     facultyDetails: AttachmentFile[];
}

export interface CourseDetailsState {
     courseDetails: CourseDetailsFormData;
     labs: LabEquipment[];
     equipmentFormData: EquipmentFormData;
     faculties: FacultyMember[];
     attachments: AttachmentSection;
     selectedCourseData: CourseData | null; // Course data from API
}

const initialState: CourseDetailsState = {
     courseDetails: {
          selectedCourse: "",
          morningSections: 0,
          eveningSections: 0,
          morningStudents: 0,
          eveningStudents: 0,
          totalStudents: 0,
          attachment: "",
     },
     labs: [],
     equipmentFormData: {},
     faculties: [],
     attachments: {
          courseTimetable: [],
          facultyDetails: [],
     },
     selectedCourseData: null,
};

const courseDetailsSlice = createSlice({
     name: "courseDetails",
     initialState,
     reducers: {
          setCourseDetails(state, action: PayloadAction<Partial<CourseDetailsFormData>>) {
               state.courseDetails = { ...state.courseDetails, ...action.payload };
          },
          setLabs(state, action: PayloadAction<LabEquipment[]>) {
               state.labs = action.payload;
          },
          setFaculties(state, action: PayloadAction<FacultyMember[]>) {
               state.faculties = action.payload;
          },
          addFaculty(state, action: PayloadAction<FacultyMember>) {
               state.faculties.push(action.payload);
          },
          updateFaculty(state, action: PayloadAction<{ index: number; faculty: FacultyMember }>) {
               state.faculties[action.payload.index] = action.payload.faculty;
          },
          removeFaculty(state, action: PayloadAction<number>) {
               state.faculties.splice(action.payload, 1);
          },
          setAttachments(state, action: PayloadAction<AttachmentSection>) {
               state.attachments = action.payload;
          },
          setCourseTimetableAttachments(state, action: PayloadAction<AttachmentFile[]>) {
               state.attachments.courseTimetable = action.payload;
          },
          setFacultyDetailsAttachments(state, action: PayloadAction<AttachmentFile[]>) {
               state.attachments.facultyDetails = action.payload;
          },
          addAttachment(state, action: PayloadAction<{ section: 'courseTimetable' | 'facultyDetails'; file: AttachmentFile }>) {
               state.attachments[action.payload.section].push(action.payload.file);
          },
          setSelectedCourseData(state, action: PayloadAction<CourseData | null>) {
               state.selectedCourseData = action.payload;
          },
          setEquipmentFormData(state, action: PayloadAction<EquipmentFormData>) {
               state.equipmentFormData = action.payload;
          },
          clearCourseDetails(state) {
               state.courseDetails = initialState.courseDetails;
               state.labs = [];
               state.equipmentFormData = {};
               state.faculties = [];
               state.attachments = {
                    courseTimetable: [],
                    facultyDetails: [],
               };
               state.selectedCourseData = null;
          },
     },
});

export const {
     setCourseDetails,
     setLabs,
     setFaculties,
     addFaculty,
     updateFaculty,
     removeFaculty,
     setAttachments,
     setCourseTimetableAttachments,
     setFacultyDetailsAttachments,
     addAttachment,
     setSelectedCourseData,
     setEquipmentFormData,
     clearCourseDetails,
} = courseDetailsSlice.actions;

export default courseDetailsSlice.reducer; 